package uk.co.nickthecoder.kyd

import uk.co.nickthecoder.glok.collections.asMutableObservableList
import uk.co.nickthecoder.glok.property.ObservableValue
import uk.co.nickthecoder.glok.property.TernaryFunction
import uk.co.nickthecoder.glok.property.boilerplate.booleanProperty
import uk.co.nickthecoder.glok.property.boilerplate.floatProperty
import uk.co.nickthecoder.glok.property.boilerplate.stringProperty
import uk.co.nickthecoder.glok.property.property
import uk.co.nickthecoder.glok.scene.Color
import uk.co.nickthecoder.glok.util.Matrix
import uk.co.nickthecoder.kyd.appearance.Appearance
import uk.co.nickthecoder.kyd.appearance.NoAppearance
import uk.co.nickthecoder.kyd.util.PixelDetection
import uk.co.nickthecoder.kyd.util.Vector2

/**
 * An [Actor]'s parent is either another [Actor] or a [Stage].
 */
interface ActorParent {
    val children: List<Actor>

    fun removeActor(actor: Actor)

    fun addActor(actor: Actor) {
        addActor(children.size, actor)
    }

    fun addActor(index: Int, actor: Actor)
}

/**
 * An [Actor] represents every item you see within the game, and is arguably the most important Kyd class,
 *
 * Even static scenery are [Actor]s.
 *
 * You may argue that `Actor` is a bad name for scenery,
 * but it makes me smile as it reminds me of a scene in _"A Midsummer Night's Dream"_.
 * There is a play within a play, and one of the actors takes on the role of a _wall_.
 * In the original :
 *
 *     _Bottom_ : let him hold his fingers thus, and through that cranny shall _Pyramus_ and _Thisbe_ whisper.
 *
 * However, in the version I saw, _Wall_ was played by a petite young actress in a leotard, and an entirely
 * different _cranny_ was chosen. While kneeling down _Pyramus_ couldn't be heard by _Thisbe_, so _Wall_ suggests
 * _Pyramus_ widen the cranny with his fingers. The look on Pyramus' face was priceless.
 * Shakespeare plays are often smutty, but they took it to another level.
 * If you are ever in London, take in a comedy at [The Globe](https://www.shakespearesglobe.com/) -
 * cheap a chips and funny as hell.
 */
class Actor(parent: ActorParent) : ActorParent {

    var parent: ActorParent = parent
        internal set

    /**
     * If you wish to find an actor by its id. There is no guarantee of uniqueness.
     * Kyd itself doesn't use this field, it exists only because it might be useful.
     * [toString] includes the [id], which may help debugging.
     *
     * You could use this for example, to give keys and locks and id. When you collect a key,
     * look for all locks with the same id, and unlock them.
     *
     * There are other ways (which may be better) to "find" an actor.
     * For example, if I want my [Director] to find the `player`,
     * I add a `registerPlayer()` method to [Director], and call it from [Behaviour.onEnteredStage]
     * or [Behaviour.onActStarted].
     */
    val idProperty by stringProperty("")
    var id by idProperty

    /**
     * Hides the actor without changing its [appearance].
     */
    val visibleProperty by booleanProperty(true)
    var visible by visibleProperty

    val appearanceProperty by property<Appearance>(NoAppearance)
    var appearance by appearanceProperty

    val behaviourProperty by property<Behaviour>(NoBehaviour)
    var behaviour by behaviourProperty

    val roleProperty by property<Role?>(null)
    var role by roleProperty

    /**
     * The position of the actor relative to its [parent].
     * For a top-level actor (whose [parent] is a [Stage]), this uses [CoordinateSystem.world].
     * But if this is a child of another [Actor], the position is relative to the parent Actor (See [CoordinateSystem.child]).
     */
    val positionProperty by vector2Property(Vector2.ZERO)
    var position by positionProperty


    /**
     * A convenient alternative to get/set [position].x
     */
    var x: Float
        get() = position.x
        set(x) {
            position = Vector2(x, position.y)
        }

    /**
     * A convenient alternative to get/set [position].y
     */
    var y: Float
        get() = position.y
        set(y) {
            position = Vector2(position.x, y)
        }

    val scaleProperty by vector2Property(Vector2.ONE)
    var scale by scaleProperty

    /**
     * A convenient way to set the x and y values of [scale].
     * If you read the value, it returns the x component of [scale].
     */
    var scaleXY
        get() = scale.x
        set(xy) {
            scale = Vector2(xy, xy)
        }

    /**
     * A convenient alternative to get/set [scale].x
     */
    var scaleX: Float
        get() = scale.x
        set(x) {
            scale = Vector2(x, scale.y)
        }

    /**
     * A convenient alternative to get/set [scale].y
     */
    var scaleY: Float
        get() = scale.y
        set(y) {
            scale = Vector2(scale.x, y)
        }

    /**
     * The angle of rotation about [position] point (measured in degrees).
     * Positive angles are anticlockwise, i.e. PI/2 radians will rotate a quart-turn anticlockwise.
     *
     * When writing games, it is usually nicer to use degrees than radians. However, when you do maths,
     * degrees are a PITA, and [angleRadians] are easier!
     *
     * See [angleRadians].
     */
    val angleDegreesProperty by floatProperty(0f)
    var angleDegrees by angleDegreesProperty

    /**
     * The angle of rotation about [position] point (measured in radians).
     * Positive angles are anticlockwise, i.e. PI/2 radians will rotate a quart-turn anticlockwise.
     *
     * See [angleDegrees]
     */
    var angleRadians: Float
        get() = angleDegrees * degreesToRadians
        set(radians) {
            angleDegrees = radians * radiansToDegrees
        }

    /**
     * Only used when placed on a [ZOrderStage].
     * It is often better to create separate [Stage]s, rather than using [zOrder].
     * For example, if you have `terrain`, `walking` and `flying` objects, create 3 stages.
     * When you do this, the [zOrder] is often not relevant.
     */
    val zOrderProperty by floatProperty(0f)
    var zOrder by zOrderProperty

    /**
     * When this [Actor] is a child (rather than being part of a [Stage]), should it be drawn BELOW its parent?
     */
    val isBelowProperty by booleanProperty(false)
    var isBelow by isBelowProperty

    /**
     * TODO This won't be needed when the editor dynamically loads the game's jar files.
     * Without this, behaviours will reset to NoRole when an act is saved.
     */
    val behaviourTypeProperty by stringProperty("")
    var behaviourType by behaviourTypeProperty

    // TODO This should be immutable from the outside?
    override val children = mutableListOf<Actor>().asMutableObservableList()

    /**
     * Removes this actor from its parent ([Stage] or another [Actor]).
     */
    fun remove() {
        parent.removeActor(this)
    }

    override fun removeActor(actor: Actor) {
        children.remove(actor)
    }

    override fun addActor(index: Int, actor: Actor) {
        if (actor.parent !== this) {
            actor.parent.removeActor(actor)
        }
        actor.parent = this
        children.add(index, actor)
    }

    /**
     * A transformation matrix used while drawing.
     */
    val localToWorldProperty: ObservableValue<Matrix> =
        TernaryFunction(positionProperty, angleDegreesProperty, scaleProperty) { position, angleDegrees, scale ->
            var t = Matrix.translation(position.x, position.y)
            if (angleDegrees != 0f) {
                t = t.rotate(angleDegrees * degreesToRadians)
            }
            if (scale != Vector2.ONE) {
                t = t.scale(scale.x, scale.y)
            }
            t
        }
    val localToWorld by localToWorldProperty

    val worldToLocalProperty: ObservableValue<Matrix> =
        TernaryFunction(positionProperty, angleDegreesProperty, scaleProperty) { position, angleDegrees, scale ->
            var t = if (scale != Vector2.ONE) {
                Matrix.scale(1 / scale.x, 1 / scale.y)
            } else {
                Matrix.IDENTITY
            }

            if (angleDegrees != 0f) {
                t *= Matrix.rotation(- angleDegrees * degreesToRadians)
            }
            t *= Matrix.translation(- position.x, - position.y)

            t
    }
    val worldToLocal by worldToLocalProperty

    /**
     * Returns our color at a given [worldPoint]. If the point is outside our bounds, it will always be
     * [Color.TRANSPARENT].
     * This is used to test if a mouse event is touching this actor, by comparing the color's alpha
     * with a threshold value.
     *
     * NOTE. I haven't done any performance testing. It *might* be more efficient
     * to first check [containsWorldPoint] and only if this returns true, then call this.
     *
     * ### Implementation notes.
     * We create a 1x1 texture. Set up a `view` matrix, such that [worldPoint] is the center of this pixel.
     * We then draw the actor to this texture (instead of the window) and then read the pixel data from
     * the texture and convert it to a [Color].
     */
    fun colorAtWorldPoint(worldPoint: Vector2) = PixelDetection.actorColorAt(this, worldPoint)

    override fun toString() = "@ $position $id"
}
